import { useState } from "react";
import { Link } from "react-router-dom";
import type { CategoryProduct } from "@/lib/catalog";
import { conditionLabel, formatPrice, timeAgo } from "@/lib/catalog";
import SmartMedia from "@/components/feature/SmartMedia";
import { useCart } from "@/hooks/useCart";
import { useToast } from "@/components/feature/Toast";

export default function ProductItemCard({
  product,
}: {
  product: CategoryProduct;
}) {
  const [favorite, setFavorite] = useState(false);
  const { addItem } = useCart();
  const { showToast } = useToast();
  const image = product.images?.[0];
  const isNew = (product.condition ?? "").toLowerCase() === "nuevo";
  const seller = product.profiles?.full_name;

  return (
    <Link
      to={`/producto/${product.id}`}
      data-product-shop
      className="glass glass-sheen group flex h-full flex-col overflow-hidden rounded-lg transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-lift"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-background-100">
        {image ? (
          <SmartMedia
            src={image}
            alt={product.title}
            videoControls={false}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-foreground-300">
            <i className="ri-image-line text-3xl"></i>
          </span>
        )}

        <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
          <span
            className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
              isNew ? "bg-secondary-500 text-white" : "bg-background-800 text-white dark:bg-background-200 dark:text-foreground-950"
            }`}
          >
            {conditionLabel(product.condition)}
          </span>
        </div>

        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            setFavorite((value) => !value);
          }}
          aria-label="Marcar como favorito"
          className="absolute right-3 top-3 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-50/90 transition-transform hover:scale-110"
        >
          <i
            className={`text-base ${
              favorite
                ? "ri-heart-fill text-accent-500"
                : "ri-heart-line text-foreground-600"
            }`}
          ></i>
        </button>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 text-sm font-medium text-foreground-900">
          {product.title}
        </h3>
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="font-heading text-lg font-bold text-foreground-950">
            {formatPrice(product.price, product.currency)}
          </span>
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              addItem(product);
              showToast("Agregado al carrito", "success");
            }}
            aria-label="Agregar al carrito"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-white transition-transform hover:scale-110 active:scale-95"
          >
            <i className="ri-shopping-cart-2-line text-base"></i>
          </button>
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 pt-3 text-xs text-foreground-500">
          <span className="flex min-w-0 items-center gap-1">
            <i className="ri-store-2-line shrink-0"></i>
            <span className="truncate">{seller ?? "Vendedor"}</span>
          </span>
          <span className="whitespace-nowrap text-foreground-400">
            {timeAgo(product.created_at)}
          </span>
        </div>
      </div>
    </Link>
  );
}